import { type SiteId } from '@solinkcorp/api-types';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { usePrevious } from '@solinkcorp/solink-components';
import { Button } from '@solinkcorp/solink-ui/components/Button';
import { Checkbox } from '@solinkcorp/solink-ui/components/Checkbox';
import { Input } from '@solinkcorp/solink-ui/components/Input';
import { Label } from '@solinkcorp/solink-ui/components/ui/label';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { ALL_LOCATIONS, WIDGET_INHERIT_VALUE } from '@solinkcorp/vd-common';
import classNames from 'classnames';
import { SearchClearButton } from 'components/SearchClearButton';
import { type TagFilter } from 'components/SettingsPage/LocationManagement/types';
import ThemeVariables from 'env/themeVariables';
import isEqual from 'lodash/isEqual';
import { Search } from 'lucide-react';
import { type SELECT_LOCATIONS_CONTEXT } from 'models/AnalyticsConstants';
import AnalyticsLogger from 'models/AnalyticsLogger';
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FixedSizeList } from 'react-window';
import { useDebouncedFunction } from 'src/hooks/useDebounce';
import useFluxStore from 'src/hooks/useFluxStore';
import { LocationStore } from 'stores/LocationStore';
import LocaleUtil from 'utils/LocaleUtil';
import { type VideoDiscoverySite } from 'utils/types';
import {
  createLocationTagSearchMap,
  getVisibleTagsById,
  type LocationTagSearchMap,
  parseLocationTagsIntoFilters,
  processLocations,
  sortLocationsByChecked,
} from '../locationSelectorUtils';
import LocationTaggingFilters from './LocationTaggingFilters';
import styles from './MultiLocationSelector.module.sass';
interface MultiLocationSelectorProps {
  /** Whether we allow users to uncheck individual locations from the selection generated by the selected tag filters */
  allowUncheckLocationFromTagFilters?: boolean;
  locations: readonly VideoDiscoverySite[];
  locale: LocaleDefinition;
  onLocationsSelected?: ({
    locationIds,
    locationTags,
  }: {
    locationIds: readonly SiteId[];
    locationTags?: readonly string[];
  }) => void;
  selectedLocationIds?: readonly SiteId[];
  selectedLocationTags?: readonly string[];
  closeLocationList: () => void;
  withInheritOption?: boolean;
  /** If the app is fetching the latest location list from API */
  isFetching?: boolean;
  isInheriting?: boolean;
  inheritOptionLabel?: string;
  inheritedLocations?: {
    locationIds: readonly SiteId[];
    locationTags: readonly string[];
  };
  context?:
    typeof SELECT_LOCATIONS_CONTEXT.GLOBAL_SELECTOR | typeof SELECT_LOCATIONS_CONTEXT.WIDGET;
  /** Caps the virtualized list so it can scroll inside a constrained popover. */
  maxListHeight?: number;
}

export default function MultiLocationSelector(props: MultiLocationSelectorProps) {
  const {
    context,
    allowUncheckLocationFromTagFilters = true,
    closeLocationList,
    locale,
    locations,
    onLocationsSelected,
    selectedLocationIds = [],
    selectedLocationTags = [],
    withInheritOption = false,
    isFetching = false,
    isInheriting = false,
    inheritOptionLabel,
    inheritedLocations,
    maxListHeight,
  } = props;
  const uniqueId = useId();
  const bodyRef = useRef<HTMLDivElement>(null);
  const [measuredBodyHeight, setMeasuredBodyHeight] = useState<number | null>(null);
  const [searchString, setSearchString] = useState('');
  const [checkedLocationIds, setCheckedLocationIds] = useState(selectedLocationIds);
  const [selectedFiltersById, setSelectedFiltersById] =
    useState<readonly string[]>(selectedLocationTags);
  const [lastCheckedLocationId, setLastCheckedLocationId] = useState('');
  const [isInheritingChecked, setIsInheritingChecked] = useState(isInheriting);
  const [visibleTagsById, setVisibleTagsById] = useState<string[]>([]);
  const holdingShift = useRef(false);
  const isMounted = useRef(false);
  const sortedLocations = useRef(locations);
  const locationTaggingFilters = useMemo(
    () => parseLocationTagsIntoFilters(locations),
    [locations],
  );

  const locationsById = useFluxStore(LocationStore, (store) => store.getLocationsById());

  const allLocationIds = useMemo(() => locations.map((location) => location.id), [locations]);

  const locationTagSearchMap = useMemo(() => createLocationTagSearchMap(locations), [locations]);

  const prevCheckedLocationIds = usePrevious(checkedLocationIds);
  const processedLocations = useMemo(() => {
    const checkedLocationIdsHasNotChanged = isEqual(checkedLocationIds, prevCheckedLocationIds);
    if (checkedLocationIdsHasNotChanged || !isMounted.current) {
      sortedLocations.current = sortLocationsByChecked(locations, checkedLocationIds);
    }
    return processLocations({
      searchString,
      locations: sortedLocations.current,
      selectedFiltersById,
    });
  }, [searchString, locations, checkedLocationIds, selectedFiltersById, prevCheckedLocationIds]);

  const handleSelectedFiltersChange = useCallback(
    (selectedFilters: string[]) => {
      setSelectedFiltersById(selectedFilters);
      const newProcessedLocations = processLocations({
        searchString,
        locations: sortedLocations.current,
        selectedFiltersById: selectedFilters,
      });
      setCheckedLocationIds(
        newProcessedLocations.length === allLocationIds.length
          ? [ALL_LOCATIONS as SiteId]
          : newProcessedLocations.map((location) => location.id),
      );
    },
    [allLocationIds.length, searchString],
  );

  const updateVisibleTagsById = useCallback(
    ({
      locationTaggingFilters,
      searchString,
      selectedFiltersById,
      locationTagSearchMap,
    }: {
      locationTaggingFilters: readonly TagFilter[];
      searchString: string;
      selectedFiltersById: readonly string[];
      locationTagSearchMap: LocationTagSearchMap;
    }) => {
      setVisibleTagsById(
        getVisibleTagsById({
          locationTaggingFilters,
          searchString,
          selectedFiltersById,
          locationTagSearchMap,
        }),
      );
    },
    [],
  );

  const debouncedUpdateVisibleTagsById = useDebouncedFunction(updateVisibleTagsById, 100);

  // Debounce the update to the visibleTagsById state update to ensure that the updates to the searchInput are prioritized
  useEffect(() => {
    debouncedUpdateVisibleTagsById({
      locationTaggingFilters,
      searchString,
      selectedFiltersById,
      locationTagSearchMap,
    });
  }, [
    locationTaggingFilters,
    searchString,
    selectedFiltersById,
    locationTagSearchMap,
    debouncedUpdateVisibleTagsById,
  ]);

  useEffect(() => {
    isMounted.current = true;
  }, []);

  const allLocationsSelected = checkedLocationIds[0] === ALL_LOCATIONS;

  const handleInheritChange = (value: boolean) => {
    setIsInheritingChecked(value);
    if (value && inheritedLocations) {
      setCheckedLocationIds(inheritedLocations.locationIds);
      setSelectedFiltersById(inheritedLocations.locationTags);
    }
  };

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchString(value);
      if (allLocationsSelected && value) {
        setCheckedLocationIds([]);
      }
    },
    [allLocationsSelected],
  );

  const handleAllLocationsChange = useCallback(() => {
    if (checkedLocationIds.length) {
      setCheckedLocationIds([]);
    } else {
      if (checkedLocationIds.includes(ALL_LOCATIONS as SiteId)) {
        return;
      }

      const newCheckedLocationIds: SiteId[] = [];
      processedLocations.forEach(({ id }) => {
        if (!checkedLocationIds.includes(id) && id !== ALL_LOCATIONS) {
          newCheckedLocationIds.push(id);
        }
      });

      if (newCheckedLocationIds.length) {
        setCheckedLocationIds(
          allLocationIds.length === newCheckedLocationIds.length + checkedLocationIds.length
            ? [ALL_LOCATIONS as SiteId]
            : [...checkedLocationIds, ...newCheckedLocationIds],
        );
      }
    }
    setLastCheckedLocationId('');
  }, [processedLocations, checkedLocationIds, allLocationIds]);

  const handleCheckboxChange = useCallback(
    (checked: boolean, locationId: SiteId) => {
      let newCheckedLocationIds: SiteId[] = [];
      if (holdingShift.current) {
        let targetPosition = 0;
        let recentPosition = 0;

        processedLocations.forEach((location, index) => {
          if (location.id === locationId) targetPosition = index;
          if (location.id === lastCheckedLocationId && lastCheckedLocationId)
            recentPosition = index;
        });

        newCheckedLocationIds = allLocationsSelected ? allLocationIds : [...checkedLocationIds];
        const filterLocations: string[] = [];

        const startPosition = targetPosition > recentPosition ? recentPosition : targetPosition;
        const endPosition = targetPosition > recentPosition ? targetPosition : recentPosition;

        for (let i = startPosition; i <= endPosition; i++) {
          const currentLocationId = processedLocations[i].id;
          if (checked) {
            if (
              !checkedLocationIds.includes(currentLocationId) &&
              currentLocationId !== ALL_LOCATIONS
            )
              newCheckedLocationIds.push(currentLocationId);
          } else {
            filterLocations.push(currentLocationId);
          }
        }
        newCheckedLocationIds = newCheckedLocationIds.filter((id) => !filterLocations.includes(id));
      } else {
        if (checked) {
          newCheckedLocationIds = [...checkedLocationIds, locationId];
        } else {
          if (allLocationsSelected) {
            newCheckedLocationIds = allLocationIds.filter((id) => locationId !== id);
          } else {
            newCheckedLocationIds = checkedLocationIds.filter((id: string) => locationId !== id);
          }
        }
      }

      if (newCheckedLocationIds.length === allLocationIds.length) {
        newCheckedLocationIds = [ALL_LOCATIONS as SiteId];
      }
      setLastCheckedLocationId(locationId);
      setCheckedLocationIds(newCheckedLocationIds);
    },
    [
      allLocationIds,
      allLocationsSelected,
      checkedLocationIds,
      holdingShift,
      processedLocations,
      lastCheckedLocationId,
    ],
  );

  const selectLocations = useCallback(() => {
    if (!checkedLocationIds?.length) {
      return;
    }

    const doLocationTagsMatchCheckedLocations =
      !!selectedFiltersById.length &&
      processedLocations.every((location) => checkedLocationIds.includes(location.id));

    if (onLocationsSelected) {
      if (isInheritingChecked) {
        onLocationsSelected({
          locationIds: [WIDGET_INHERIT_VALUE as SiteId],
          locationTags: [WIDGET_INHERIT_VALUE],
        });
      } else {
        onLocationsSelected({
          locationIds: checkedLocationIds,
          locationTags: doLocationTagsMatchCheckedLocations ? selectedFiltersById : [],
        });
        const analyticLocations = checkedLocationIds.map((id) => ({
          id,
          name: locationsById?.[id].name,
        }));

        AnalyticsLogger.selectLocations({
          locations: analyticLocations,
          locationTags: doLocationTagsMatchCheckedLocations ? selectedFiltersById : undefined,
          context,
        });
      }
    }
    closeLocationList();
  }, [
    context,
    locationsById,
    checkedLocationIds,
    onLocationsSelected,
    closeLocationList,
    processedLocations,
    selectedFiltersById,
    isInheritingChecked,
  ]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      holdingShift.current = true;
    }
  }, []);

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      holdingShift.current = false;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  const hasLocationTaggingFilters = !!locationTaggingFilters.length;

  const showingAllLocations = processedLocations.length === allLocationIds.length;
  let allLocationsLabelText = showingAllLocations
    ? locale.locationSelector.selectAllLocation
    : locale.locationSelector.selectLocation;
  if (allLocationsSelected) {
    allLocationsLabelText = LocaleUtil.getLocalizedStr({
      string: locale.locationSelector.allLocationsSelected,
      replacements: {
        number: allLocationIds?.length,
      },
    });
  } else if (checkedLocationIds?.length > 0) {
    allLocationsLabelText = LocaleUtil.getLocalizedStr({
      string: locale.locationSelector.locationsSelected,
      replacements: {
        number: checkedLocationIds?.length,
      },
    });
  }

  const {
    locationSelectorBodyPadding,
    locationSelectorLocationsWidth,
    locationSelectorLocationHeight,
  } = ThemeVariables;

  const maxLocationListHeight =
    maxListHeight ?? document.documentElement.clientHeight * 0.5 - locationSelectorBodyPadding * 2;

  const allProcessedlocationsListHeight =
    processedLocations.length * locationSelectorLocationHeight;

  const locationListWidth = hasLocationTaggingFilters
    ? locationSelectorLocationsWidth - locationSelectorBodyPadding
    : locationSelectorLocationsWidth - 2 * locationSelectorBodyPadding;

  const cappedListHeight = measuredBodyHeight ?? maxLocationListHeight;

  const locationListHeight =
    allProcessedlocationsListHeight > cappedListHeight
      ? cappedListHeight
      : allProcessedlocationsListHeight;

  const locationSelectorWrapperClass = classNames(styles.locationSelectorWrapper, {
    [styles.locationFilters]: hasLocationTaggingFilters,
    [styles.noLocationResults]: !processedLocations.length,
    [styles.aspen]: true,
  });

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const update = () => {
      const next = el.clientHeight;
      if (next > 0) setMeasuredBodyHeight(next);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [processedLocations.length, hasLocationTaggingFilters]);

  const disableSelectLocations = isInheritingChecked;
  const disableSingleSelect =
    disableSelectLocations ||
    (selectedFiltersById.length > 0 && !allowUncheckLocationFromTagFilters);

  return (
    // tw is required here to apply tailwind styles in widget settings
    <div className={cn('tw', locationSelectorWrapperClass)}>
      {withInheritOption && (
        <div className={styles.locationSelectorHeader}>
          <div className="flex items-center gap-3">
            <Checkbox
              className={styles.inherit}
              id="inherit-locations"
              name="inherit-locations"
              checked={isInheritingChecked}
              onCheckedChange={handleInheritChange}
            />
            <Label htmlFor="inherit-locations">{inheritOptionLabel}</Label>
          </div>
        </div>
      )}
      <div className={cn(styles.locationSelectorHeader, hasLocationTaggingFilters && 'gap-3')}>
        <div className={styles.locationSelectorHeaderCol}>
          <Input
            disabled={disableSelectLocations}
            autoComplete="off"
            className={cn(styles.locationSearchInput, 'w-full')}
            name="search-input"
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={locale.general.search}
            leftIcon={<Search />}
            aria-label={locale.general.search}
            value={searchString}
            data-testid="location-picker-search-input"
            autoFocus
            rightIcon={
              searchString ? (
                <SearchClearButton
                  label={locale.general.clear}
                  onClick={() => handleSearchChange('')}
                />
              ) : undefined
            }
          />
        </div>
        <div className={styles.locationSelectorHeaderCol}>
          <div className={cn('flex items-center gap-3', { 'pt-3': !hasLocationTaggingFilters })}>
            <Checkbox
              checked={
                allLocationsSelected ? true : checkedLocationIds.length ? 'indeterminate' : false
              }
              onCheckedChange={handleAllLocationsChange}
              name="all-locations"
              id="all-locations"
              data-testid="location-picker-all-locations-status"
              className={styles.selectAllLocationsCheckbox}
              disabled={!processedLocations.length || disableSingleSelect}
            />
            <Label htmlFor="all-locations">{allLocationsLabelText}</Label>
          </div>
        </div>
      </div>
      <div ref={bodyRef} className={styles.locationSelectorBody}>
        {processedLocations.length || visibleTagsById.length ? (
          <>
            {hasLocationTaggingFilters && (
              <LocationTaggingFilters
                disabled={disableSelectLocations}
                selectedFiltersById={selectedFiltersById}
                onSelectedFiltersByIdChange={handleSelectedFiltersChange}
                filters={locationTaggingFilters}
                visibleTagsById={visibleTagsById}
              />
            )}
            <div>
              {processedLocations.length ? (
                <FixedSizeList
                  className={styles.checkboxItems}
                  itemData={{
                    locations: processedLocations,
                    allLocationsSelected,
                    handleCheckboxChange,
                    checkedLocationIds,
                  }}
                  itemCount={processedLocations.length}
                  height={locationListHeight}
                  width={locationListWidth}
                  itemSize={locationSelectorLocationHeight}
                  itemKey={(index: number) =>
                    `${uniqueId}-${processedLocations?.[index].id || index}`
                  }
                >
                  {({ data, index, style }) => (
                    <Location
                      data={data}
                      disableSelect={disableSingleSelect}
                      index={index}
                      style={style}
                      uniqueId={uniqueId}
                    />
                  )}
                </FixedSizeList>
              ) : (
                <div className={styles.noLocationsMatch} style={{ width: locationListWidth }}>
                  {locale.locationSelector.noLocationsMatchFilters}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className={styles.noResults}>{locale.error.noResults}</div>
        )}
      </div>
      <div className={styles.locationSelectorFooter}>
        <div
          className={cn(
            styles.locationSelectorFooterContent,
            'flex items-center',
            isFetching ? 'justify-between' : 'justify-end',
          )}
        >
          {isFetching && (
            <div className={styles.locationFetchingMsg}>
              {locale.locationSelector.fetchingLatest}...
            </div>
          )}
          <div className="flex w-auto gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={closeLocationList}
              data-testid="location-picker-cancel"
            >
              {locale.general.cancel}
            </Button>
            <Button
              type="button"
              size="sm"
              className="tw text-interaction-primary-content-default"
              disabled={!checkedLocationIds.length}
              onClick={selectLocations}
              data-testid="location-picker-apply"
            >
              {locale.general.apply}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Location({
  disableSelect,
  data,
  index,
  style,
  uniqueId,
}: {
  disableSelect: boolean;
  data: {
    locations: readonly VideoDiscoverySite[];
    allLocationsSelected: boolean;
    checkedLocationIds: readonly SiteId[];
    handleCheckboxChange: (checked: boolean, id: SiteId) => void;
  };
  index: number;
  style: React.CSSProperties;
  uniqueId: string;
}) {
  const location = data.locations[index];
  const checked = data.allLocationsSelected || data.checkedLocationIds?.includes(location.id);
  const handleCheckboxChangeWithId = (value: boolean) =>
    data.handleCheckboxChange(value, location.id);
  const name = `${uniqueId}-${location.id}`;
  return (
    <div className={styles.locationItem} style={style}>
      <div className="flex items-start gap-2">
        <Checkbox
          id={name}
          name={name}
          checked={checked}
          onCheckedChange={handleCheckboxChangeWithId}
          data-testid={`${location.name}-status`}
          disabled={disableSelect}
        />
        <div className="flex flex-col">
          <Label htmlFor={name} className="text-sm font-medium leading-4">
            {location.name}
          </Label>
          <p className="text-xs text-foreground-secondary">{location.address}</p>
        </div>
      </div>
    </div>
  );
}
